import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Clock,
  FileText,
  Globe,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Copy,
  Check,
  MessageSquare,
  Shield,
} from "lucide-react";
import {
  Application,
  ApplicationStatus,
  useUpdateApplication,
} from "../../../lib/api/adminApplications";

interface ApplicationDetailsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  application: Application | null;
}

const ApplicationDetailsSidebar: React.FC<ApplicationDetailsSidebarProps> = ({
  isOpen,
  onClose,
  application,
}) => {
  const [adminNotes, setAdminNotes] = useState("");
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showConfirmAction, setShowConfirmAction] =
    useState<ApplicationStatus | null>(null);

  const { mutate: updateApplication, isPending } = useUpdateApplication();

  useEffect(() => {
    if (application) {
      setAdminNotes(application.adminNotes || "");
    }
  }, [application]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleStatusUpdate = (status: ApplicationStatus) => {
    if (!application) return;
    updateApplication(
      {
        id: application._id,
        payload: { status, ...(adminNotes && { adminNotes }) },
      },
      { onSuccess: () => setShowConfirmAction(null) }
    );
  };

  const handleSaveNotes = () => {
    if (!application) return;
    updateApplication({ id: application._id, payload: { adminNotes } });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusConfig = (status: ApplicationStatus) => {
    const configs = {
      pending: {
        bg: "bg-amber-50",
        text: "text-amber-700",
        border: "border-amber-200",
        icon: Clock,
      },
      reviewed: {
        bg: "bg-blue-50",
        text: "text-blue-700",
        border: "border-blue-200",
        icon: AlertCircle,
      },
      shortlisted: {
        bg: "bg-purple-50",
        text: "text-purple-700",
        border: "border-purple-200",
        icon: CheckCircle,
      },
      rejected: {
        bg: "bg-red-50",
        text: "text-red-700",
        border: "border-red-200",
        icon: XCircle,
      },
      hired: {
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        border: "border-emerald-200",
        icon: CheckCircle,
      },
    };
    return configs[status] || configs.pending;
  };

  const getExperienceLabel = (exp: string) => {
    const labels: Record<string, string> = {
      entry: "Entry Level",
      "1-2": "1-2 Years",
      "3-5": "3-5 Years",
      "5+": "5+ Years",
    };
    return labels[exp] || exp;
  };

  const getAvailabilityLabel = (avail: string) => {
    const labels: Record<string, string> = {
      immediate: "Immediate",
      "1-2-weeks": "1-2 Weeks",
      "2-4-weeks": "2-4 Weeks",
      "1-month": "1+ Month",
    };
    return labels[avail] || avail;
  };

  if (!application) return null;

  const statusConfig = getStatusConfig(application.status);
  const StatusIcon = statusConfig.icon;

  const sidebarContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999]"
          />

          {/* Sidebar */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full sm:w-[480px] md:w-[540px] bg-white shadow-2xl z-[9999] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  Application Details
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {application.applicationId}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
              {/* Applicant Info */}
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center text-white text-lg font-semibold flex-shrink-0">
                  {application.firstName[0]}
                  {application.lastName[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-gray-900">
                    {application.firstName} {application.lastName}
                  </h3>
                  <p className="text-gray-600">{application.jobTitle}</p>
                  <div
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium mt-2 ${statusConfig.bg} ${statusConfig.text}`}
                  >
                    <StatusIcon className="w-3.5 h-3.5" />
                    {application.status.charAt(0).toUpperCase() +
                      application.status.slice(1)}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-500" />
                  Contact Information
                </h4>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{application.email}</span>
                    </div>
                    <button
                      onClick={() => handleCopy(application.email, "email")}
                      className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                    >
                      {copiedField === "email" ? (
                        <Check className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{application.phone}</span>
                    </div>
                    <button
                      onClick={() => handleCopy(application.phone, "phone")}
                      className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                    >
                      {copiedField === "phone" ? (
                        <Check className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      {application.location}
                    </span>
                  </div>
                </div>
              </div>

              {/* Application Details */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-gray-500" />
                  Application Details
                </h4>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Experience</p>
                    <p className="text-sm font-medium text-gray-900">
                      {getExperienceLabel(application.experience)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Availability</p>
                    <p className="text-sm font-medium text-gray-900">
                      {getAvailabilityLabel(application.availability)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Applied On</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatDate(application.createdAt)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Signup Code</p>
                    <div className="flex items-center gap-1">
                      <p className="text-sm font-mono font-medium text-[#8B52FF]">
                        {application.signupCode}
                      </p>
                      <button
                        onClick={() =>
                          handleCopy(application.signupCode, "code")
                        }
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                      >
                        {copiedField === "code" ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <Copy className="w-3 h-3 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resume */}
              {application.resumeUrl && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-gray-500" />
                    Resume
                  </h4>
                  <a
                    href={application.resumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:border-[#8B52FF] hover:text-[#8B52FF] transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Resume
                  </a>
                </div>
              )}

              {/* Cover Letter */}
              {application.coverLetter && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-gray-500" />
                    Cover Letter
                  </h4>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {application.coverLetter}
                  </p>
                </div>
              )}

              {/* Geo Location */}
              {application.geoCountry && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
                    <Globe className="w-4 h-4 text-gray-500" />
                    Location Verification
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">Detected Location</p>
                      <p className="font-medium text-gray-900">
                        {application.geoCity}, {application.geoRegion}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Country</p>
                      <p className="font-medium text-gray-900">
                        {application.geoCountry} ({application.geoCountryCode})
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Timezone</p>
                      <p className="font-medium text-gray-900">
                        {application.geoTimezone}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">IP Address</p>
                      <p className="font-mono text-xs text-gray-600">
                        {application.geoIp}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-gray-500" />
                  Admin Notes
                </h4>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes about this application..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] resize-none"
                />
                {adminNotes !== (application.adminNotes || "") && (
                  <button
                    onClick={handleSaveNotes}
                    disabled={isPending}
                    className="mt-2 px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium text-gray-700 transition-colors disabled:opacity-50"
                  >
                    Save Notes
                  </button>
                )}
                {application.reviewedAt && (
                  <p className="text-xs text-gray-400 mt-2">
                    Last reviewed: {formatDate(application.reviewedAt)}
                    {application.reviewedBy && ` by ${application.reviewedBy}`}
                  </p>
                )}
              </div>
            </div>

            {/* Action Footer */}
            <div className="p-4 sm:p-6 border-t border-gray-100 bg-white">
              {showConfirmAction ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 text-center">
                    Are you sure you want to mark this application as{" "}
                    <span className="font-semibold">{showConfirmAction}</span>?
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowConfirmAction(null)}
                      className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(showConfirmAction)}
                      disabled={isPending}
                      className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                        showConfirmAction === "rejected"
                          ? "bg-red-500 hover:bg-red-600"
                          : showConfirmAction === "hired"
                            ? "bg-emerald-500 hover:bg-emerald-600"
                            : "bg-[#8B52FF] hover:bg-[#7B42EF]"
                      }`}
                    >
                      {isPending && (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )}
                      Confirm
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Quick Actions */}
                  {/* {application.status === "pending" && (
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowConfirmAction("reviewed")}
                        className="flex-1 px-4 py-2.5 bg-blue-50 text-blue-700 rounded-xl text-sm font-medium hover:bg-blue-100 transition-colors"
                      >
                        Mark Reviewed
                      </button>
                      <button
                        onClick={() => setShowConfirmAction("shortlisted")}
                        className="flex-1 px-4 py-2.5 bg-purple-50 text-purple-700 rounded-xl text-sm font-medium hover:bg-purple-100 transition-colors"
                      >
                        Shortlist
                      </button>
                    </div>
                  )} */}

                  {(application.status === "reviewed" ||
                    application.status === "shortlisted" ||
                    application.status === "pending") && (
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowConfirmAction("hired")}
                        className="flex-1 px-4 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Hire
                      </button>
                      <button
                        onClick={() => setShowConfirmAction("rejected")}
                        className="flex-1 px-4 py-2.5 bg-red-50 text-red-700 rounded-xl text-sm font-medium hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  )}

                  {/* {application.status === "pending" && (
                    <button
                      onClick={() => setShowConfirmAction("rejected")}
                      className="w-full px-4 py-2.5 border border-red-200 text-red-600 rounded-xl text-sm font-medium hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Reject Application
                    </button>
                  )} */}

                  {(application.status === "rejected" ||
                    application.status === "hired") && (
                    <div
                      className={`text-center py-3 rounded-xl ${
                        application.status === "hired"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      <p className="text-sm font-medium">
                        This application has been{" "}
                        {application.status === "hired"
                          ? "approved"
                          : "rejected"}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(sidebarContent, document.body);
};

export default ApplicationDetailsSidebar;
